<script>
// import Translations from "./insert-me-to-translate.vue";

import fill from "lodash/fill";
import sum from 'lodash/sum';
import has from 'lodash/has';
import map from 'lodash/map';
import get from 'lodash/get';
import maxBy from 'lodash/maxBy';
import values from 'lodash/values';
import isUndefined from 'lodash/isUndefined';

const loadPica = () => import(/* webpackChunkName: "chunk-pica", webpackPrefetch: true */ 'pica');

const loadBowser = () => import(/* webpackChunkName: "chunk-bowser" */ 'bowser');

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", err => reject(err));
    img.src = src;
  });
};

function pReadFileAsArrayBuffer(file){
  return new Promise((resolve, reject) => {
    let fr = new FileReader();
    fr.addEventListener("load", () => resolve(fr.result));
    fr.addEventListener("error", err => reject(err))
    fr.readAsArrayBuffer(file);
  });
};

export default {
    components: {
        ImageEditor: () => import(/* webpackChunkName: "chunk-image-editor" */ './image-editor'),
        Draggable: () => import(/* webpackChunkName: "chunk-vuedraggable", webpackPrefetch: true */ 'vuedraggable'),
        UiAlert: () => import(/* webpackChunkName: "chunk-ui-alert" */ './ui/ui-alert.vue'),
    },

    model: {
        prop: 'value',
        event: 'change'
    },

    props: {
        value: {
            type: Array,
            default: () => {
                return [];
            }
        },
        initUploadImageUrl: {
            type: String,
            required: true
        },
        initUploadImageMaxWidth: {
            type: Number,
            required: true
        },
        initUploadImageMaxHeight: {
            type: Number,
            required: true
        },
        initUploadImageMinWidth: {
            type: Number,
            required: true
        },
        initUploadImageMinHeight: {
            type: Number,
            required: true
        },
        initNumberOfAllowedImages: {
            type: Number,
            required: true
        },
        initPreviewImagesNumber: {
            type: Number,
            required: true
        },
        gdprFlagActive: {
            type: Boolean,
            required: true
        },
        flagImageUpload: {
            type: Boolean,
            required: true
        },
        privateCode: {
            type: String,
            required: false
        },
        temporaryCode: {
            type: String,
            required: false
        },
    },

    data: function() {
        return {
            // see: https://github.com/SortableJS/Vue.Draggable/issues/743
            draggableForceFallback: false,
            uploadBoxStyle: '',
            savedImagesLoadingProgress: 0,

            imagesArray: JSON.parse(JSON.stringify(this.value)),    // get and copy v-model value
            browserImages: {},
            isUploading: false,
            uploadFlag:false,
            disableMouseEvents: false,
            cropperOpenDisableSorting: false,  // set to true when the cropper modal is open
            forceDisableSorting: true,

            savedImagesLoadingComplete: false,

            bytesToUpload: null,
            uploadedBytesPerFile: null,
            uploadProgress: 0,

            // error message (see lightbox code)
            lightboxInvalidImageMessageContent: null,
            lightboxInvalidImageErrorCodes: new Set(),
            ERROR_CODE_INVALID_FORMAT: "1",
            ERROR_CODE_INVALID_CONTENT: "2",
            ERROR_CODE_MULTIPLE_FACES: "3",


            // image upload settings
            maxImageSize: 10 * 1024 * 1024,
            postRequestMaxSize: 3 * 1024 * 1024,

            // see: https://github.com/nodeca/pica#resizefrom-to-options---promise
            pica: null,
            picaResizeOptions: {
                unsharpAmount: 160,
                unsharpRadius: 0.6,
                unsharpThreshold: 1,
                alpha: true,
            },
            picaToBlobQuality: 1  //between 0 and 1
        }
    },

    computed: {
        enabledPhotoFlag(){
            return this.gdprFlagActive || this.flagImageUpload;
        },

        uploadBoxClass(){
            if(this.imagesArray.length === 0) {
                return "col-12";
            }else{
                return "col-md-3 col-xs-6 col-sm-6";
            }
        },
        rootDivCssClass() {
            return 'pointer-events:' +  (this.disableMouseEvents ? 'none' : 'auto') + ';';
        },
        imageBoxClass(){
            return (this.imageFlagDisable ? 'boxdisable' : '');
        },

        imageFlagDisable: function(){
            return (!this.uploadFlag && this.enabledPhotoFlag);
        },

        coverIndexes: function(){
            const arr = [];
            for(let i=0; i < this.imagesArray.length; i++) {
                if (this.imagesArray[i].isCover) {
                    arr.push(i);
                }
            }
            return arr;
        },

        disableSorting() {
            return this.forceDisableSorting || this.$root.isMobile || this.imageFlagDisable || this.imagesArray.length <= 1 || this.cropperOpenDisableSorting;
        },
    },

    methods: {
        loadImageInput(e) {
            const files = e.srcElement.files;
            if(files.length > 0) {
                this.appendImageEditors(files);
            }
        },

        appendImageEditors(fileList) {
            // console.log('appendImageEditors');
            this.$emit('image-upload-started');

            let displayErrorLightbox = false;
            let duplicatedFilenames = [];   //used to show duplicated images error message

            if(this.imagesArray.length + fileList.length > this.initNumberOfAllowedImages) {
                displayErrorLightbox = true;
            }

            const filesToUpload = [];
            // check files before continuing upload
            Array.from(fileList).forEach((pictureFile, index)  => {
                let error = false;
                if (filesToUpload.length + this.imagesArray.length +1 > this.initNumberOfAllowedImages){
                    // too many images
                    error = true;
                } else if (!pictureFile.type.match('image.*')) {
                    error = true;
                } else if (
                    //check file extension
                    !pictureFile.name.toLowerCase().endsWith('.jpg') &&
                    !pictureFile.name.toLowerCase().endsWith('.jpeg') &&
                    !pictureFile.name.toLowerCase().endsWith('.png')
                ) {
                    error = true;
                } else if (pictureFile.size > this.maxImageSize) {
                    // check whether the size is greater than the size limit
                    error = true;
                    this.lightboxInvalidImageErrorCodes.add(this.ERROR_CODE_INVALID_FORMAT);
                }

                if(error) {
                    displayErrorLightbox = true;
                } else {
                    filesToUpload.push(pictureFile);
                }
            });

            this.isUploading = true;
            const resizePromises = [];

            // create an array of promises
            // each promise will resolve to an object like this:
            // {
            //    src: <the blob object used in cropper and preview, created with createObjectURL>,
            //    file: <the file or blob object, eventually resized to max width/height that will be uploaded>
            //    filename: <filename of the image>
            // }
            filesToUpload.forEach(pictureFile  => {
                // create object for the browser
                const pictureURL = URL.createObjectURL(pictureFile);
                // get file name
                const filename = pictureFile.name;

                // create new canvas, used as destination for resized image
                const offScreenCanvasDest = document.createElement('canvas');


                resizePromises.push(new Promise((resolve, reject) => {

                    pReadFileAsArrayBuffer(pictureFile.slice(0,4)).then(buffer => {
                        //check file types using initial bytes of the file
                        // i.e read first 4 bytes and transform them in hex

                        const arr = new Uint8Array(buffer);
                        let headerHex = "";
                        for(let i = 0; i < arr.length; i++) {
                           headerHex += arr[i].toString(16);
                        }

                        const isJpeg = headerHex.startsWith('ffd8ff'); // jpeg https://filesignatures.net/index.php?page=all&order=SIGNATURE&sort=DESC&alpha=J
                        const isPng = headerHex === '89504e47';  // png

                        if( !(isJpeg || isPng) ) {
                            this.lightboxInvalidImageErrorCodes.add(this.ERROR_CODE_INVALID_FORMAT);
                            return Promise.resolve({
                                resolveValue: {
                                    errorMessage: `Incorrect image format: ${headerHex}`,
                                },
                            });
                        }

                        // resize image if bigger than width/height limits
                        return loadImage(pictureURL);
                    }).then(resImg => {
                        const resolveValue = get(resImg, 'resolveValue', undefined);
                        if(!isUndefined(resolveValue)) {
                            return Promise.resolve({resolveValue});
                        }

                        const orginalWidth = resImg.width;
                        const orginalHeight = resImg.height;

                        if(orginalWidth < this.initUploadImageMinWidth || orginalHeight < this.initUploadImageMinHeight) {
                            this.lightboxInvalidImageErrorCodes.add(this.ERROR_CODE_INVALID_FORMAT);
                            return Promise.resolve({
                                resolveValue: {
                                    errorMessage: `Image size is smaller than minimum size required: ${this.initUploadImageMinWidth}x${this.initUploadImageMinHeight}px`,
                                },
                            });
                        }

                        // calculate aspect ratio
                        const imgRatio = Math.min(
                            this.initUploadImageMaxWidth/orginalWidth,
                            this.initUploadImageMaxHeight/orginalHeight
                        );

                        if(imgRatio >= 1) {
                            // don't upscale small images
                            // return original file
                            return Promise.resolve({
                                resolveValue: {
                                    src: pictureURL,
                                    file: pictureFile,
                                    filename,
                                },
                            });
                        }

                        offScreenCanvasDest.width  = orginalWidth * imgRatio;
                        offScreenCanvasDest.height = orginalHeight * imgRatio;


                        // Load Pica (if not already loaded)
                        if (!this.pica) {
                          return loadPica().then(picaModule => {
                            this.pica = new picaModule.default({features: ['js']});
                            return {
                              picaLoaded:true,
                              picaInstance: this.pica,
                              image: resImg
                            };
                          });
                        } else {
                          return Promise.resolve({
                              picaLoaded:false,
                              picaInstance: null,
                              image: resImg
                            });
                        }
                    }).then(result => {
                        const resolveValue = get(result, 'resolveValue', undefined);
                        if(!isUndefined(resolveValue)) {
                            return Promise.resolve({resolveValue});
                        }

                        if(result.picaLoaded){
                            this.pica = result.picaInstance;
                        }
                        return Promise.resolve(result.image);
                    }).then(result => {
                        const resolveValue = get(result, 'resolveValue', undefined);
                        if(!isUndefined(resolveValue)) {
                            return Promise.resolve({resolveValue});
                        }

                        // resize
                        return this.pica.resize(result, offScreenCanvasDest, this.picaResizeOptions);
                    }).then(result => {
                        const resolveValue = get(result, 'resolveValue', undefined);
                        if(!isUndefined(resolveValue)) {
                            return Promise.resolve({resolveValue});
                        }

                        return this.pica.toBlob(offScreenCanvasDest, pictureFile.type, this.picaToBlobQuality);
                    }).then(blob => {
                        const resolveValue = get(blob, 'resolveValue', undefined);
                        if(!isUndefined(resolveValue)) {
                            return Promise.resolve({resolveValue});
                        }

                        return Promise.resolve({
                            resolveValue: {
                                src: URL.createObjectURL(blob),
                                file: blob,
                                filename,
                            },
                        });
                    }).then(result => {
                        if(has(result, 'resolveValue')){
                            resolve(result.resolveValue);
                        }else{
                            reject(`missing resolveValue in ${result}`);
                        }
                    }).catch((error) => {
                        reject(error);
                    });
                }));
            });

            // await promises
            Promise.all(resizePromises)
            .then((imagesToUpload) => {
                if(imagesToUpload.length === 0) {
                    throw new Error('No images to upload');
                }

                // initialize progress counters
                this.bytesToUpload = 0;
                this.uploadedBytesPerFile = fill(Array(imagesToUpload.length), 0);

                const uploadPromises = [];
                for(let idx=0; idx<imagesToUpload.length; idx++) {
                    const imgToUpload = imagesToUpload[idx];
                    if(has(imgToUpload, 'errorMessage')){
                        displayErrorLightbox = true;
                        // skip image
                        continue;
                    }

                    this.bytesToUpload += imgToUpload.file.size;
                    uploadPromises.push(
                        new Promise((resolve, reject) => {
                            //create form data and add image file
                            const formData = new FormData();
                            formData.append('pictureFiles', imgToUpload.file);
                            formData.append('privateCode', this.privateCode);
                            formData.append('temporaryCode', this.temporaryCode);

                            // upload one image
                            this.$http.post(
                                this.initUploadImageUrl,
                                formData,
                                {
                                    headers: {
                                        'Content-Type': 'multipart/form-data',
                                        'X-CSRFToken': this.$cookies.get('csrftoken')
                                    },
                                    onUploadProgress: progressEvent => {
                                        this.uploadedBytesPerFile[idx] = progressEvent.loaded;
                                        const up = 100 * sum(this.uploadedBytesPerFile) / this.bytesToUpload;
                                        this.uploadProgress = Math.min(Math.round(up), 100);
                                    },
                                }
                            ).then((response) => {
                                // response contains a single image
                                return resolve({
                                    filename: imgToUpload.filename,
                                    imageSrc: imgToUpload.src,
                                    serverResponse: response.data.pictures[0]
                                });
                            }).catch((error) => {
                                console.error(`upload post error for file ${imgToUpload.filename}: ${error}`);
                                return resolve({
                                    error: error?.response?.data,
                                    filename: imgToUpload.filename,
                                });
                            });
                        })
                    );
                }

                // return upload promises
                return Promise.all(uploadPromises);
            }).then((responseImages) => {
                // create images array
                for(let i=0; i<responseImages.length; i++) {
                    const responseImage = responseImages[i];
                    // ignore images with errors
                    if(has(responseImage, 'error')) {
                        if(has(responseImage.error, 'error_code')) {
                            this.lightboxInvalidImageErrorCodes.add(responseImage.error.error_code);
                            displayErrorLightbox = true;
                        }
                        continue;
                    }

                    // prevent loading of duplicated images
                    const imgUuid = responseImage.serverResponse.uuid;
                    const imgHistogram = responseImage.serverResponse.histogram;
                    const imgFingerprint = responseImage.serverResponse.fingerprint;

                    if(this.imagesArray.find(function(im){ return im.histogram === imgHistogram; })){
                        duplicatedFilenames.push(responseImage.filename);
                        displayErrorLightbox = true;
                        continue;
                    }

                    // add image to v-model array
                    this.imagesArray.push({
                        uuid: imgUuid,
                        histogram: imgHistogram,
                        fingerprint: imgFingerprint,
                        // isNew: responseImage.serverResponse.isNew,
                        src: responseImage.serverResponse.url,
                        isCover: false,
                        crop: null,
                        rotate: 0,
                    });

                    // add image to cropper src images array
                    this.$set(this.browserImages, imgUuid, {
                        browserSrc: responseImage.imageSrc,
                        loadCompleted: false,
                    });

                    // add to cover images if possible
                    if (this.imagesArray.length > 0 && this.coverIndexes.length < this.initPreviewImagesNumber) {
                        this.imagesArray[this.imagesArray.length-1].isCover = true
                    }
                }

            }).catch((error) => {
                // console.error(error);
                this.showErrorLightbox();
            }).finally(() => {
                this.$emit('image-upload-completed');
                this.bytesToUpload = null;
                this.uploadedBytesPerFile = null;
                this.uploadProgress = 0;
                this.isUploading = false;
                if (displayErrorLightbox) {
                    if(duplicatedFilenames.length === 1){
                        const fmts = gettext('The file %s has already been uploaded.');
                        this.lightboxInvalidImageMessageContent = interpolate(fmts, [duplicatedFilenames[0]]);
                    }else if(duplicatedFilenames.length > 1){
                        this.lightboxInvalidImageMessageContent = gettext('The selected files are existing. We recommend you to add more photos.');
                    }
                    this.showErrorLightbox();
                }
            });
        },

        setCover(index) {
            // remove image from covers
            if(this.imagesArray[index].isCover){
                if (this.initPreviewImagesNumber > 1) {
                    this.imagesArray[index].isCover = false;
                }
                return;
            }

            // add image to covers
            if(this.coverIndexes.length >= this.initPreviewImagesNumber){
                // remove last image from covers
                this.imagesArray[this.coverIndexes[this.initPreviewImagesNumber-1]].isCover = false;
            }
            // set image as cover
            this.imagesArray[index].isCover = true;
        },

        deleteImage(index) {
            const wasCover = this.imagesArray[index].isCover;
            const imgUuid = this.imagesArray[index].uuid;

            this.$delete(this.imagesArray, index);
            this.$delete(this.browserImages, imgUuid);

            if(wasCover) {
                if(this.coverIndexes.length === 0 && this.imagesArray.length > 0) {
                    // use another image as cover
                    if (index === 0) {
                       this.imagesArray[0].isCover = true;
                    } else {
                        this.imagesArray[index - 1].isCover = true;
                    }
                }
            }
        },

        showErrorLightbox() {
            $(this.$refs.lightboxInvalidImage).modal('show');
        },

        closeErrorLightbox(){
            $(this.$refs.lightboxInvalidImage).modal('hide');

            // reset error message to default
            this.lightboxInvalidImageMessageContent = null;
            this.lightboxInvalidImageErrorCodes = new Set();
        },

        updateImageEditData(index, editData) {
            this.imagesArray[index].rotate = editData.rotate;
            this.imagesArray[index].crop = editData.crop;
        },

        cropperModalShown(index) {
            this.cropperOpenDisableSorting = true;
            this.disableMouseEvents = true;
        },

        cropperModalHidden(index) {
            this.cropperOpenDisableSorting = false;
            this.disableMouseEvents = false;
        },

        onImageLoadCompleted(imgUuid, {updatePreviewFn}) {
            this.browserImages[imgUuid].loadCompleted = true;
            this.browserImages[imgUuid].updatePreviewFn = updatePreviewFn;
            if(this.savedImagesLoadingComplete) {
                // call update function
                this.$nextTick().then(()=>{
                    updatePreviewFn();
                });
            }
        },

        onClientSizeData(imgUuid, clientSizeData) {
            this.$set(this.browserImages[imgUuid], 'clientSize', clientSizeData);
        },
    },

    created() {
        loadBowser()
            .then(({default: Bowser}) => {
                const browser = Bowser.getParser(window.navigator.userAgent);
                const forceFallback = !!browser.satisfies({
                    'MacOS': {
                        safari: ">1",
                    },
                });
                this.draggableForceFallback = forceFallback;
            })
            .catch((error) => {
                console.error(error);
                this.draggableForceFallback = true;
            });
    },

    mounted() {
        this.uploadFlag = false;
        this.savedImagesLoadingComplete = true;
        // initialize browser images object, for uploaded images
        for(let i=0; i<this.imagesArray.length; i++){
            this.$set(this.browserImages, this.imagesArray[i].uuid, {browserSrc: null, loadCompleted: false, savedImage: true});
            this.uploadFlag = true;
            this.savedImagesLoadingComplete = false;
        }

        // preventing page from redirecting
        $("html").on("dragover", (e) => {
            e.preventDefault();
            e.stopPropagation();
        });

        $("html").on("drop", (e) => {
            e.preventDefault();
            e.stopPropagation();
        });

        // Drag enter
        $(this.$refs.imageBox).on('dragenter', '.upload-drop-zone', (e) => {
            e.stopPropagation();
            e.preventDefault();
        });

        // Drag over
        $(this.$refs.imageBox).on('dragover', '.upload-drop-zone', (e) => {
            e.stopPropagation();
            e.preventDefault();
        });

        // Drop
        $(this.$refs.imageBox).on('drop', '.upload-drop-zone', (e) => {
            e.stopPropagation();
            e.preventDefault();
            if(!this.enabledPhotoFlag || this.uploadFlag){
                const files = e.originalEvent.dataTransfer.files;
                if(files.length > 0) {
                    this.appendImageEditors(files);
                }
            }else{
                // console.log($(this.$refs.uploadFlag));
            }
        });


        $(this.$refs.imageBox).on('click', '.upload-drop-zone', (e) => {
            if(this.enabledPhotoFlag && !this.uploadFlag){
                this.PopoverItem = $(this.$refs.uploadFlagLabel);
                this.PopoverItem.popover("show");
                $('.popover').addClass('popover-no-time-selected');
                this.PopoverItem[0].scrollIntoView({block: "center"});

                e.stopPropagation();
                e.preventDefault();
            }
        });

        //window.addEventListener('click', function(e){
        window.addEventListener('click', (e)=>{
            // console.log('click',$(this.$refs.uploadDropZone));
            if (!$(this.$refs.uploadDropZone).is(e.target)){
                // console.log('click outside');
                this.PopoverItem = $(this.$refs.uploadFlagLabel);
                this.PopoverItem.popover("hide");
            }
        });

    },


    watch: {
        imagesArray: {
            deep: true,
            handler(val){
                this.$emit('input', val);
            }
        },

        browserImages: {
            deep: true,
            immediate: true,
            handler(val){
                const imgValues = values(val);

                // set "upload image box" height
                const maxHeight = maxBy(imgValues, (im) => get(im, 'clientSize.totalHeight', undefined));
                if(maxHeight) {
                    this.uploadBoxStyle = `height: ${maxHeight.clientSize.totalHeight}px;`;
                } else {
                    this.uploadBoxStyle = `height: auto;`;
                }

                // load progress
                const savedImages = imgValues.filter((img) => has(img, 'savedImage'));
                if(savedImages.length === 0 || this.savedImagesLoadingComplete) {
                    this.savedImagesLoadingProgress = 100;
                    return;
                }

                // only counts images that are loaded in mounted hook (have savedImage key)
                this.savedImagesLoadingProgress = Math.round(savedImages.filter((img) => img.loadCompleted).length / savedImages.length  * 100);
            },
        },

        savedImagesLoadingProgress: {
            handler(val){
                if(val === 100) {
                    // fire updatePreview on images that were loaded in mounted hook
                    const updateFunctions = map(values(this.browserImages), 'updatePreviewFn');
                    this.$nextTick().then(()=>{
                        updateFunctions.forEach(f => f());
                    });
                    this.savedImagesLoadingComplete = true;
                }
            }
        },
    }
}
</script>
